import { memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  X,
  Minimize2,
  Maximize2,
  Loader2,
  Eye,
  BrainCircuit
} from 'lucide-react';
import { useNowPlaying } from '../contexts/NowPlayingContext';
import { useReadingPreferences, LEAD_LETTER_OPTIONS } from '../contexts/ReadingPreferencesContext';

/**
 * Format seconds to MM:SS display
 */
const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Progress slider - chapter-relative.
 * Shows progress within the current chapter, not the whole book.
 */
const ProgressSlider = memo(function ProgressSlider({
  chapterRelativeTime,
  chapterDuration,
  chapterStartTime,
  seekToGlobalTime,
}) {
  return (
    <div className="flex-1 flex items-center gap-4">
      <span className="text-sm text-muted-foreground tabular-nums min-w-[60px]">
        {formatTime(chapterRelativeTime)}
      </span>
      <Slider
        value={[chapterRelativeTime]}
        max={chapterDuration || 100}
        step={0.1}
        onValueChange={(value) => {
          if (seekToGlobalTime) {
            seekToGlobalTime(chapterStartTime + value[0]);
          }
        }}
        className="flex-1"
        aria-label="Chapter progress"
      />
      <span className="text-sm text-muted-foreground tabular-nums min-w-[60px]">
        {formatTime(chapterDuration)}
      </span>
    </div>
  );
});

/**
 * Media controls bar - reads everything from NowPlayingContext.
 *
 * Always rendered globally in App.jsx when there's an active book.
 * Behaviour:
 * - On the /now-playing route: shows minimize button → goes to /my-library
 * - On any other route: shows maximize button → goes to /now-playing
 * - Close button always clears the book and goes to /my-library
 */
const MediaControlsBar = memo(function MediaControlsBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    manifest,
    bookId,
    isPlaying,
    isBuffering,
    playbackSpeed,
    visibleChapters,
    currentChapterIndex,
    _currentChapter,
    chapterStartTime,
    chapterDuration,
    chapterRelativeTime,
    togglePlayPause,
    skipForward,
    skipBackward,
    adjustSpeed,
    seekToGlobalTime,
    seekToChapter,
    clearBook,
  } = useNowPlaying();
  const { leadLetters, dyslexiaMode, setLeadLetters, setDyslexiaMode } = useReadingPreferences();

  // Don't render if no book
  if (!bookId || !manifest) return null;

  const isOnNowPlayingRoute = location.pathname === '/now-playing';

  const handleMinimize = () => {
    navigate('/my-library');
  };

  const handleMaximize = () => {
    navigate('/now-playing');
  };

  const handleClose = () => {
    clearBook();
    navigate('/my-library');
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[60] bg-background border-t shadow-lg pointer-events-auto">
      <div className="px-4 py-3 md:px-8 md:py-5">
        {/* Mobile: vertical stack so 320px-wide reflow works (WCAG 1.4.10).
         * Desktop (md+): horizontal row matching the original layout.
         * Wrappers with `md:contents` dissolve on desktop, putting their
         * children back as direct flex items of this row; `md:order-last`
         * pushes the action buttons to the right end on desktop. */}
        <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
          {/* Top row on mobile: book info on the left, close/minimize on the right. */}
          <div className="flex items-center justify-between gap-2 md:contents">
            {/* Book Info — clickable to maximize the player to /now-playing,
             * disabled when already on /now-playing. */}
            <button
              type="button"
              className="flex-1 min-w-0 md:flex-initial md:w-56 md:flex-shrink-0 text-left md:text-center hover:opacity-80 transition-opacity disabled:cursor-default disabled:hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded"
              onClick={!isOnNowPlayingRoute ? handleMaximize : undefined}
              disabled={isOnNowPlayingRoute}
              aria-label={isOnNowPlayingRoute ? `Now playing: ${manifest.title}` : `Open reader for ${manifest.title}`}
            >
              <h3 className="font-semibold text-base truncate">{manifest.title}</h3>
              <p className="text-sm text-muted-foreground truncate">by {manifest.author}</p>
            </button>

            {/* Action Buttons — appear top-right on mobile, far-right on desktop. */}
            <div className="flex items-center gap-2 md:order-last md:ml-auto flex-shrink-0">
              {isOnNowPlayingRoute ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-12 w-12 md:h-14 md:w-14"
                  onClick={handleMinimize}
                  aria-label="Minimize player"
                >
                  <Minimize2 className="h-6 w-6 md:h-7 md:w-7" />
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-12 w-12 md:h-14 md:w-14"
                  onClick={handleMaximize}
                  aria-label="Maximize player"
                >
                  <Maximize2 className="h-6 w-6 md:h-7 md:w-7" />
                </Button>
              )}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-12 w-12 md:h-14 md:w-14"
                onClick={handleClose}
                aria-label="Close player"
              >
                <X className="h-6 w-6 md:h-7 md:w-7" />
              </Button>
            </div>
          </div>

          {/* Playback Controls — centered on mobile, inline on desktop. */}
          <div className="flex items-center justify-center md:justify-start gap-3">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-14 w-14"
              onClick={skipBackward}
              aria-label="Skip backward 5 seconds"
            >
              <SkipBack className="h-7 w-7" />
            </Button>

            <Button
              type="button"
              size="icon"
              className="h-16 w-16"
              onClick={togglePlayPause}
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isBuffering ? <Loader2 className="h-9 w-9 animate-spin" /> : isPlaying ? <Pause className="h-9 w-9" /> : <Play className="h-9 w-9" />}
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-14 w-14"
              onClick={skipForward}
              aria-label="Skip forward 5 seconds"
            >
              <SkipForward className="h-7 w-7" />
            </Button>
          </div>

          {/* Progress Bar - chapter-relative */}
          <ProgressSlider
            chapterRelativeTime={chapterRelativeTime}
            chapterDuration={chapterDuration}
            chapterStartTime={chapterStartTime}
            seekToGlobalTime={seekToGlobalTime}
          />

          {/* Chapter Selector — full-width on mobile. */}
          {visibleChapters.length > 0 && (
            <Select
              value={String(currentChapterIndex)}
              onValueChange={(value) => {
                seekToChapter(parseInt(value, 10));
              }}
            >
              <SelectTrigger className="w-full md:w-64 h-14 text-sm" aria-label="Jump to chapter">
                <SelectValue placeholder="Chapter" />
              </SelectTrigger>
              <SelectContent className="max-h-[400px] overflow-y-auto" side="top" align="start">
                {visibleChapters.map((ch, i) => (
                  <SelectItem key={i} value={String(i)}>
                    {ch.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Secondary controls — share a row on mobile, dissolve on desktop. */}
          <div className="flex items-center gap-2 md:contents">
            {/* Speed Control */}
            <Select value={playbackSpeed} onValueChange={adjustSpeed}>
              <SelectTrigger className="flex-1 md:flex-initial md:w-28 h-14 text-sm" aria-label="Playback speed">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0.5">0.5×</SelectItem>
                <SelectItem value="0.75">0.75×</SelectItem>
                <SelectItem value="1">1×</SelectItem>
                <SelectItem value="1.25">1.25×</SelectItem>
                <SelectItem value="1.5">1.5×</SelectItem>
                <SelectItem value="2">2×</SelectItem>
              </SelectContent>
            </Select>

            {/* Reading Focus (Eye-Voice Span lead in letters) */}
            <Select
              value={String(leadLetters)}
              onValueChange={(v) => setLeadLetters(parseInt(v, 10))}
            >
              <SelectTrigger className="flex-1 md:flex-initial md:w-28 h-14 text-sm" aria-label="Reading focus — letters ahead">
                <Eye className="h-4 w-4 mr-1 shrink-0" aria-hidden="true" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LEAD_LETTER_OPTIONS.map((n) => (
                  <SelectItem key={n} value={String(n)}>{n} letters</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Dyslexia mode toggle */}
            <Button
              type="button"
              variant={dyslexiaMode ? 'default' : 'ghost'}
              size="icon"
              className="h-14 w-14 flex-shrink-0"
              onClick={() => setDyslexiaMode(!dyslexiaMode)}
              aria-label={dyslexiaMode ? 'Turn off dyslexia-friendly mode' : 'Turn on dyslexia-friendly mode'}
              aria-pressed={dyslexiaMode}
              title="Dyslexia-friendly mode"
            >
              <BrainCircuit className="h-6 w-6" aria-hidden="true" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});

export default MediaControlsBar;
